import React from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";

const CHUNK_RELOAD_KEY = "mapit_chunk_reload_attempted";

const isChunkLoadError = (error) => {
  if (!error) return false;

  const errorMessage = `${error?.name || ""} ${error?.message || ""}`.toLowerCase();

  return (
    errorMessage.includes("chunkloaderror") ||
    errorMessage.includes("loading chunk") ||
    errorMessage.includes("failed to fetch dynamically imported module") ||
    errorMessage.includes("importing a module script failed")
  );
};

class ChunkLoadErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  componentDidMount() {
    window.addEventListener("error", this.handleWindowError);
    window.addEventListener("unhandledrejection", this.handleUnhandledRejection);
  }

  componentWillUnmount() {
    window.removeEventListener("error", this.handleWindowError);
    window.removeEventListener("unhandledrejection", this.handleUnhandledRejection);
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      isChunkError: isChunkLoadError(error),
    };
  }

  componentDidCatch(error) {
    if (isChunkLoadError(error)) {
      this.recoverFromChunkError();
    }
  }

  handleWindowError = (event) => {
    const error = event?.error || new Error(event?.message || "");
    if (isChunkLoadError(error)) {
      this.setState({ hasError: true, isChunkError: true });
      this.recoverFromChunkError();
    }
  };

  handleUnhandledRejection = (event) => {
    const error = event?.reason;
    if (isChunkLoadError(error)) {
      this.setState({ hasError: true, isChunkError: true });
      this.recoverFromChunkError();
    }
  };

  recoverFromChunkError = () => {
    const wasReloaded = sessionStorage.getItem(CHUNK_RELOAD_KEY) === "true";
    if (!wasReloaded) {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, "true");
      window.location.reload();
    }
  };

  // User-initiated reload = the last-resort escape hatch, so it also tears down
  // any service worker and Cache Storage before reloading. Today's worker is
  // inert (no fetch handler, see public/service-worker.js) so this is a no-op,
  // but it guarantees the button can always recover a client that is stuck on a
  // cached build if a caching worker is ever introduced.
  // Deliberately NOT done in recoverFromChunkError — that path is automatic and
  // its timing should stay unchanged.
  handleReload = async () => {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);

    try {
      if ("serviceWorker" in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((registration) => registration.unregister()));
      }
      if (window.caches) {
        const keys = await window.caches.keys();
        await Promise.all(keys.map((key) => window.caches.delete(key)));
      }
    } catch (error) {
      // Cleanup is best-effort; never block the reload on it.
      console.warn("Service worker / cache cleanup failed:", error);
    }

    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (!this.state.isChunkError) {
      return (
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 2,
          }}
        >
          <Box sx={{ textAlign: "center", maxWidth: 460 }}>
            <Typography variant="h6" sx={{ mb: 1.5 }}>
              Something went wrong
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              An unexpected error occurred. Please reload to continue.
            </Typography>
            <Button variant="contained" onClick={this.handleReload}>
              Reload page
            </Button>
          </Box>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
        }}
      >
        <Box sx={{ textAlign: "center", maxWidth: 460 }}>
          <Typography variant="h6" sx={{ mb: 1.5 }}>
            Connection changed while loading the page
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please reload to continue.
          </Typography>
          <Button variant="contained" onClick={this.handleReload}>
            Reload page
          </Button>
        </Box>
      </Box>
    );
  }
}

export default ChunkLoadErrorBoundary;